/**
 * @file Standalone Recalls route shell.
 */

import { appData, state } from "../../app/state/state.js";
import { canAccessView } from "../../app/routing/boardAccess.js";
import { bindNativeFieldBehavior } from "../../shared/forms/nativeFieldStyles.js";
import {
    attachRecallsRouteListeners,
    detachRecallsMonthBucketListeners,
    loadRecallsMonthBucket,
} from "./recallsDataListeners.js";
import {
    clearRecallsAdvancedSearch,
    clearRecallsClosedRouteCache,
    clearRecallsFilters,
    getSelectedRecallsExportIds,
    ensureRecallsDataBuckets,
    recallsRouteState,
    restrictRecallsExportSelection,
    setRecallsFilter,
    setRecallsExportMonthSelected,
    setRecallsExportRowSelected,
    setRecallsExportSelectionMode,
    setRecallsShowClosed,
    setRecallsOpenControlMenu,
    setRecallsOpenFilterKey,
    setRecallsSort,
    setRecallsShowPreviousMonths,
    toggleRecallsDraftsExpansion,
    toggleRecallsMonthExpansion,
    toggleRecallsYearHeading,
} from "./recallsState.js";
import {
    renderRecallsControls,
    renderRecallsDraftsTableSection,
    renderRecallsFooterControls,
    renderRecallsTable,
    renderRecallsTableState,
    getVisibleRecallsExportIds,
} from "./recallsTable.js";
import { openRecallAdvancedSearchModal } from "./recallsAdvancedSearchModal.js";
import { bindRecallsInlineEditActions } from "./recallsInlineEditing.js";
import { openRecallsExportModal } from "./recallsExport.js";
import { bindRecallsEditActions } from "./recallsEditModal.js";
import { bindRecallsMobileReadActions } from "./recallsMobileReadModal.js";
import { bindRecallsNotesActions } from "./recallsNotesModal.js";
import {
    renderMobileRouteHeaderActions,
} from "../../shared/ui/mobileRouteHeaderActions.js";
import {
    bindRecallDraftTableActions,
    openRecallQuickAddModal,
} from "./recallsQuickAddModal.js";
import {
    getLocalRecallsConfigIssues,
    renderRecallsConfigIncompleteNotice,
} from "./recallsConfigIncomplete.js";

let recallsControlOutsideAbort = null;
let recallsSearchDebounceTimer = null;
let pendingRecallsSearchFocusState = null;

function getRecallsScrollState(container) {
    if (container?.dataset.recallsRendered !== "true") {return null;}
    return {
        left: window.scrollX,
        top: window.scrollY,
    };
}

function restoreRecallsScrollPosition(scrollState) {
    if (!scrollState) {return;}
    requestAnimationFrame(() => {
        window.scrollTo({
            left: scrollState.left,
            top: Math.min(
                scrollState.top,
                Math.max(
                    0,
                    document.documentElement.scrollHeight - window.innerHeight,
                ),
            ),
        });
    });
}

function renderRecallsContent() {
    if (recallsRouteState.listenerStatus === "loading") {
        return renderRecallsTableState({
            loading: true,
            message: "Loading Recalls...",
        });
    }

    if (recallsRouteState.listenerStatus === "error") {
        return renderRecallsTableState({
            message: "Recalls could not be loaded. Check permissions and Firestore indexes.",
            tone: "danger",
        });
    }

    return renderRecallsTable();
}

function syncExportGroupCheckboxes(container) {
    container.querySelectorAll("[data-recalls-export-month], [data-recalls-export-advanced]")
        .forEach((checkbox) => {
            const isIndeterminate =
                checkbox.dataset.recallsExportIndeterminate === "true";
            checkbox.indeterminate = isIndeterminate;
            const label = checkbox.closest("[data-native-checkbox-label]");
            const marker = label?.querySelector("[data-native-checkbox-marker]");
            const icon = marker?.querySelector("[data-native-checkbox-icon]");
            if (!marker || !icon || !isIndeterminate) {return;}
            marker.style.backgroundColor = "var(--dfc-brand-primary)";
            marker.style.borderColor = "var(--dfc-brand-primary)";
            marker.style.color = "var(--dfc-brand-primary-contrast)";
            icon.textContent = "remove";
            icon.classList.remove("opacity-0");
        });
}

function getRecallsFilterValues(value) {
    const values = Array.isArray(value) ? value : [value];
    return [...new Set(values
        .map((entry) => String(entry || "").trim())
        .filter((entry) => entry && entry !== "all"))];
}

function queueRecallsSearchFocusRestore(searchInput = null) {
    const activeInput = searchInput ||
        document.querySelector("#recalls-board [data-recalls-filter=\"search\"]");
    if (!activeInput) {
        pendingRecallsSearchFocusState = null;
        return;
    }

    pendingRecallsSearchFocusState = {
        selectionStart: activeInput.selectionStart ?? activeInput.value.length,
        selectionEnd: activeInput.selectionEnd ?? activeInput.value.length,
    };
}

function restorePendingRecallsSearchFocus(container) {
    if (!pendingRecallsSearchFocusState) {return;}
    const searchInput = container.querySelector("[data-recalls-filter=\"search\"]");
    if (!searchInput) {
        pendingRecallsSearchFocusState = null;
        return;
    }

    requestAnimationFrame(() => {
        searchInput.focus({ preventScroll: true });
        try {
            searchInput.setSelectionRange(
                pendingRecallsSearchFocusState.selectionStart,
                pendingRecallsSearchFocusState.selectionEnd,
            );
        } catch {
            // Ignore unsupported selection restoration edge cases.
        }
        pendingRecallsSearchFocusState = null;
    });
}

function updateRecallsSearchFieldState(searchInput) {
    const wrapper = searchInput?.closest("[data-recalls-search-wrapper]");
    const clearButton = wrapper?.querySelector("[data-recalls-clear-search]");
    const icon = wrapper?.querySelector("[data-recalls-search-icon]");
    const isActive = Boolean(searchInput?.value?.trim());

    wrapper?.classList.toggle("border-2", isActive);
    wrapper?.classList.toggle("border-[var(--dfc-brand-accent)]", isActive);
    wrapper?.classList.toggle("border", !isActive);
    wrapper?.classList.toggle("border-gray-200", !isActive);
    icon?.classList.toggle("text-[var(--dfc-brand-accent)]", isActive);
    icon?.classList.toggle("text-gray-500", !isActive);
    clearButton?.classList.toggle("hidden", !isActive);
}

function applyRecallsSearch({ force = false } = {}) {
    queueRecallsSearchFocusRestore();
    const nextDraft = (recallsRouteState.searchDraft || "").trim();
    const currentSearch = recallsRouteState.filters?.search || "";
    if (!nextDraft) {
        if (!currentSearch) {return;}
        setRecallsFilter("search", "");
        renderRecallsView();
        return;
    }

    if (!force && nextDraft.length < 3) {return;}
    if (currentSearch === nextDraft) {return;}
    setRecallsFilter("search", nextDraft);
    renderRecallsView();
}

function queueRecallsSearchApply() {
    if (recallsSearchDebounceTimer) {
        clearTimeout(recallsSearchDebounceTimer);
    }
    recallsSearchDebounceTimer = setTimeout(() => {
        applyRecallsSearch();
        recallsSearchDebounceTimer = null;
    }, 220);
}

function bindRecallsControlOutsideClose(container) {
    recallsControlOutsideAbort?.abort();
    recallsControlOutsideAbort = new AbortController();
    document.addEventListener("pointerdown", (event) => {
        if (!container.isConnected) {
            recallsControlOutsideAbort?.abort();
            recallsControlOutsideAbort = null;
            return;
        }
        if (!recallsRouteState.openControlMenu) {return;}
        if (container.querySelector("[data-recalls-controls]")?.contains(event.target)) {
            return;
        }
        setRecallsOpenControlMenu(null);
        renderRecallsView();
    }, { signal: recallsControlOutsideAbort.signal });
}

async function loadClosedRowsForVisibleRecallsMonths(container) {
    const visibleMonthKeys = Array.from(
        container.querySelectorAll(
            "[data-recalls-month-section][data-recalls-prefetch-closed=\"true\"]",
        ),
    )
        .map((section) => section.dataset.recallsMonthSection)
        .filter(Boolean);

    await Promise.all(visibleMonthKeys.map((monthKey) =>
        loadRecallsMonthBucket(monthKey, {
            includeActive: false,
            onData: renderRecallsView,
            showClosed: recallsRouteState.showClosed,
        })));
}

function bindRecallsShellActions(container) {
    bindRecallDraftTableActions(container, renderRecallsView);
    bindRecallsInlineEditActions(container, renderRecallsView);
    bindRecallsEditActions(container, renderRecallsView);
    bindRecallsMobileReadActions(container);
    bindRecallsNotesActions(container);
    bindNativeFieldBehavior(container);
    syncExportGroupCheckboxes(container);

    container.querySelectorAll("[data-recalls-action=\"quick-add\"]")
        .forEach((button) => button.addEventListener("click", () => {
            openRecallQuickAddModal({ onSuccess: renderRecallsView });
        }));

    container.querySelector("[data-recalls-open-settings]")
        ?.addEventListener("click", async () => {
            state.settingsReturnContext = { tab: "recalls" };
            const { navigate } = await import("../../app/routing/navigationController.js");
            navigate("settings");
        });

    container.querySelector("[data-recalls-toggle-drafts]")
        ?.addEventListener("click", (event) => {
            toggleRecallsDraftsExpansion({
                currentlyExpanded:
                    event.currentTarget.getAttribute("aria-expanded") === "true",
            });
            renderRecallsView();
        });

    container.querySelector("[data-recalls-advanced-search-open]")
        ?.addEventListener("click", () => {
            openRecallAdvancedSearchModal({
                criteria: recallsRouteState.advancedSearch?.criteria || {},
                onComplete: renderRecallsView,
            });
        });

    container.querySelector("[data-recalls-advanced-search-clear]")
        ?.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            clearRecallsAdvancedSearch();
            renderRecallsView();
        });

    container.querySelector("[data-recalls-clear-search]")
        ?.addEventListener("click", (event) => {
            const searchInput = event.currentTarget
                ?.closest("[data-recalls-search-wrapper]")
                ?.querySelector("[data-recalls-filter=\"search\"]");
            if (recallsSearchDebounceTimer) {
                clearTimeout(recallsSearchDebounceTimer);
                recallsSearchDebounceTimer = null;
            }
            recallsRouteState.searchDraft = "";
            queueRecallsSearchFocusRestore(searchInput);
            setRecallsFilter("search", "");
            renderRecallsView();
        });

    container.querySelectorAll("[data-recalls-control-trigger]")
        .forEach((button) => {
            button.addEventListener("click", () => {
                const menuId = button.dataset.recallsControlTrigger;
                setRecallsOpenControlMenu(
                    recallsRouteState.openControlMenu === menuId ? null : menuId,
                );
                renderRecallsView();
            });
        });

    bindRecallsControlOutsideClose(container);

    container.querySelectorAll("[data-recalls-filter=\"search\"]").forEach((control) => {
        updateRecallsSearchFieldState(control);
        control.addEventListener("input", (event) => {
            recallsRouteState.searchDraft = event.target.value;
            updateRecallsSearchFieldState(event.target);
            queueRecallsSearchFocusRestore(event.target);
            queueRecallsSearchApply();
        });
        control.addEventListener("keydown", (event) => {
            if (event.key !== "Enter") {return;}
            event.preventDefault();
            applyRecallsSearch({ force: true });
        });
        control.addEventListener("focusout", () => {
            applyRecallsSearch({ force: true });
        });
    });

    container.querySelectorAll("[data-recalls-sort-option]")
        .forEach((button) => {
            button.addEventListener("click", () => {
                const nextField = button.dataset.recallsSortOption;
                const currentField = recallsRouteState.sort?.field || "status";
                const currentDirection = recallsRouteState.sort?.direction || "asc";
                setRecallsOpenControlMenu("sort");
                setRecallsSort(
                    nextField,
                    nextField === currentField && currentDirection !== "desc" ?
                        "desc" :
                        "asc",
                );
                renderRecallsView();
            });
        });

    container.querySelector("[data-recalls-sort-clear]")
        ?.addEventListener("click", () => {
            setRecallsOpenControlMenu("sort");
            setRecallsSort("status", "asc");
            renderRecallsView();
        });

    container.querySelectorAll("[data-recalls-control-clear]")
        .forEach((button) => {
            button.addEventListener("click", (event) => {
                event.preventDefault();
                event.stopPropagation();
                const menuId = button.dataset.recallsControlClear;
                if (menuId === "sort") {
                    setRecallsOpenControlMenu(null);
                    setRecallsSort("status", "asc");
                    renderRecallsView();
                    return;
                }
                if (menuId === "filter") {
                    setRecallsOpenControlMenu(null);
                    clearRecallsFilters();
                    renderRecallsView();
                }
            });
        });

    container.querySelectorAll("[data-recalls-filter-category]")
        .forEach((button) => {
            button.addEventListener("click", () => {
                setRecallsOpenFilterKey(button.dataset.recallsFilterCategory);
                renderRecallsView();
            });
        });

    container.querySelector("[data-recalls-filter-back]")
        ?.addEventListener("click", () => {
            setRecallsOpenFilterKey(null);
            renderRecallsView();
        });

    container.querySelectorAll("[data-recalls-filter-option]")
        .forEach((control) => {
            const handleFilterOption = () => {
                const filterKey = control.dataset.recallsFilterOption;
                const filterValue = control.dataset.recallsFilterValue;
                const currentValues = getRecallsFilterValues(
                    recallsRouteState.filters?.[filterKey],
                );
                const nextValues = filterValue === "all" ?
                    [] :
                    (currentValues.includes(filterValue) ?
                        currentValues.filter((value) => value !== filterValue) :
                        [...currentValues, filterValue]);
                setRecallsFilter(filterKey, nextValues);
                setRecallsOpenFilterKey(filterKey);
                renderRecallsView();
            };
            control.addEventListener(
                control instanceof HTMLInputElement ? "change" : "click",
                handleFilterOption,
            );
        });

    container.querySelector("[data-recalls-show-closed]")
        ?.addEventListener("change", (event) => {
            setRecallsShowClosed(event.target.checked);
            if (recallsRouteState.showClosed !== true) {
                detachRecallsMonthBucketListeners();
                clearRecallsClosedRouteCache();
                restrictRecallsExportSelection(getVisibleRecallsExportIds());
            }
            renderRecallsView();
            if (recallsRouteState.showClosed === true) {
                void loadClosedRowsForVisibleRecallsMonths(container);
            }
        });

    container.querySelector("[data-recalls-show-previous-months]")
        ?.addEventListener("change", (event) => {
            setRecallsShowPreviousMonths(event.target.checked);
            renderRecallsView();
        });

    container.querySelector("[data-recalls-export-toggle]")
        ?.addEventListener("click", () => {
            setRecallsExportSelectionMode(
                recallsRouteState.exportSelection?.active !== true,
            );
            renderRecallsView();
        });

    container.querySelector("[data-recalls-export-cancel]")
        ?.addEventListener("click", () => {
            setRecallsExportSelectionMode(false);
            renderRecallsView();
        });

    container.querySelectorAll("[data-recalls-export-open]")
        .forEach((button) => button.addEventListener("click", () => {
            const visibleSelectedIds = getSelectedRecallsExportIds()
                .filter((recallId) =>
                    getVisibleRecallsExportIds().includes(recallId));
            restrictRecallsExportSelection(visibleSelectedIds);
            openRecallsExportModal({
                recallIds: visibleSelectedIds,
                onSuccess: () => {
                    setRecallsExportSelectionMode(false);
                    renderRecallsView();
                },
            });
        }));

    container.querySelector("[data-recalls-clear-filters]")
        ?.addEventListener("click", () => {
            setRecallsOpenControlMenu("filter");
            clearRecallsFilters();
            renderRecallsView();
        });

    container.querySelectorAll("[data-recalls-export-row]")
        .forEach((checkbox) => {
            checkbox.addEventListener("change", (event) => {
                setRecallsExportRowSelected(
                    event.target.dataset.recallsExportRow,
                    event.target.checked,
                );
                renderRecallsView();
            });
        });

    container.querySelectorAll("[data-recalls-export-month]")
        .forEach((checkbox) => {
            checkbox.addEventListener("change", (event) => {
                const rowIds = (event.target.dataset.recallsExportMonthIds || "")
                    .split(",")
                    .filter(Boolean);
                setRecallsExportMonthSelected(rowIds, event.target.checked);
                renderRecallsView();
            });
        });

    container.querySelectorAll("[data-recalls-export-advanced]")
        .forEach((checkbox) => {
            checkbox.addEventListener("change", (event) => {
                const rowIds = (event.target.dataset.recallsExportAdvancedIds || "")
                    .split(",")
                    .filter(Boolean);
                setRecallsExportMonthSelected(rowIds, event.target.checked);
                renderRecallsView();
            });
        });

    container.querySelectorAll("[data-recalls-toggle-month]")
        .forEach((button) => {
            button.addEventListener("click", () => {
                if (button.disabled) {return;}
                const monthKey = button.dataset.recallsToggleMonth;
                const willExpand = button.getAttribute("aria-expanded") !== "true";
                toggleRecallsMonthExpansion(button.dataset.recallsToggleMonth, {
                    currentlyExpanded:
                        button.getAttribute("aria-expanded") === "true",
                });
                if (willExpand) {
                    renderRecallsView();
                    void loadRecallsMonthBucket(monthKey, {
                        onData: renderRecallsView,
                        showClosed: recallsRouteState.showClosed,
                    });
                    return;
                }
                renderRecallsView();
            });
        });

    container.querySelectorAll("[data-recalls-toggle-year]")
        .forEach((button) => {
            button.addEventListener("click", () => {
                toggleRecallsYearHeading(button.dataset.recallsToggleYear, {
                    currentlyExpanded:
                        button.getAttribute("aria-expanded") === "true",
                });
                renderRecallsView();
            });
        });

}

/**
 * Renders the standalone Recalls route.
 * @returns {void}
 */
export function renderRecallsView() {
    const container = document.getElementById("recalls-board");
    if (!container) {
        console.error("Recalls board container not found.");
        return;
    }

    if (!canAccessView(state.currentUserProfile, "recalls")) {
        container.innerHTML = `
            <div class="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-800">
                Access denied: Recalls view requires permission.
            </div>
        `;
        return;
    }

    ensureRecallsDataBuckets();
    const scrollState = getRecallsScrollState(container);
    const configIssues = getLocalRecallsConfigIssues(appData.config || {});
    attachRecallsRouteListeners({
        onData: () => {
            if (state.currentView === "recalls" && !state.selectedItemId) {
                renderRecallsView();
            }
        },
        showClosed: recallsRouteState.showClosed,
        showPreviousMonths: recallsRouteState.showPreviousMonths,
    });

    container.innerHTML = `
        <div class="space-y-5">
            <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div class="flex items-center justify-between gap-3 md:block">
                    <h2 class="text-2xl font-semibold text-gray-900">Recalls Board</h2>
                    ${renderMobileRouteHeaderActions()}
                </div>
                ${renderRecallsControls()}
            </div>
            ${renderRecallsConfigIncompleteNotice(configIssues)}
            ${renderRecallsDraftsTableSection()}
            <div class="space-y-4">
                ${renderRecallsContent()}
                ${renderRecallsFooterControls()}
            </div>
        </div>
    `;
    container.dataset.recallsRendered = "true";
    bindRecallsShellActions(container);
    restorePendingRecallsSearchFocus(container);
    restoreRecallsScrollPosition(scrollState);
}
